/**
 * Settings page for managing excluded users.
 *
 * @module pages/SettingsPage
 */

import { useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { Card, Badge } from '../components/ui';
import { useExcludedUsers, useAddExcludedUser, useRemoveExcludedUser } from '../hooks';
import { useSettings } from '../context/SettingsContext';
import { formatDate, cn } from '../lib/utils';

/**
 * Settings page component.
 *
 * @returns Settings page element
 */
export function SettingsPage() {
  const { data: rules, isLoading } = useExcludedUsers();
  const addRule = useAddExcludedUser();
  const removeRule = useRemoveExcludedUser();
  const { hideInternalStats, setHideInternalStats } = useSettings();

  const [type, setType] = useState<'domain' | 'email'>('email');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      const trimmed = value.trim().toLowerCase();
      if (!trimmed) return;

      // Basic validation
      if (type === 'email' && !trimmed.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }
      if (type === 'domain' && trimmed.includes('@')) {
        setError('Enter the domain without @ (e.g. "claritysearch.ai")');
        return;
      }

      addRule.mutate(
        { type, value: trimmed },
        {
          onSuccess: () => setValue(''),
          onError: (err) => setError(err.message),
        },
      );
    },
    [type, value, addRule],
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (!window.confirm('Remove this exclusion rule? Internal users matching it will be counted in dashboard stats until it is re-added.')) {
        return;
      }
      removeRule.mutate(id, {
        onError: (err) => setError(err.message),
      });
    },
    [removeRule],
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage internal user exclusions for dashboard statistics</p>
      </div>

      {/* Add new rule */}
      <Card title="Add Exclusion Rule">
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as 'domain' | 'email')}
            className="input w-full sm:w-40"
          >
            <option value="email">Email</option>
            <option value="domain">Domain</option>
          </select>
          <input
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            placeholder={type === 'domain' ? 'claritysearch.ai' : 'user@example.com'}
            className="input flex-1"
          />
          <button
            type="submit"
            disabled={addRule.isPending || !value.trim()}
            className="btn-primary whitespace-nowrap"
          >
            {addRule.isPending ? 'Adding...' : 'Add Rule'}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-error-600">{error}</p>}
      </Card>

      {/* Current rules */}
      <Card title="Current Exclusion Rules" padding="none">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : rules && rules.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Value</th>
                <th>Added</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <Badge
                      variant={rule.type === 'domain' ? 'info' : 'neutral'}
                      size="sm"
                    >
                      {rule.type}
                    </Badge>
                  </td>
                  <td>
                    <span className="font-medium text-gray-900">
                      {rule.type === 'domain' ? `@${rule.value}` : rule.value}
                    </span>
                  </td>
                  <td className="text-gray-500">{formatDate(rule.created_at)}</td>
                  <td className="text-right">
                    <button
                      onClick={() => handleRemove(rule.id)}
                      disabled={removeRule.isPending}
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm text-error-600 hover:text-error-700 hover:bg-error-50 rounded-md transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="p-6 text-gray-500 text-center">
            No exclusion rules configured. Add one above to exclude internal users from dashboard counts.
          </p>
        )}
      </Card>

      {/* Display Preferences */}
      <Card title="Display Preferences">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">Hide internal stats</p>
            <p className="text-sm text-gray-500">
              Hide the internal user/business counts from dashboard stat cards
            </p>
          </div>
          <button
            role="switch"
            aria-checked={hideInternalStats}
            aria-label="Hide internal stats"
            onClick={() => setHideInternalStats(!hideInternalStats)}
            className={cn(
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              hideInternalStats ? 'bg-primary-600' : 'bg-gray-200'
            )}
          >
            <span
              className={cn(
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                hideInternalStats ? 'translate-x-5' : 'translate-x-0'
              )}
            />
          </button>
        </div>
      </Card>
    </div>
  );
}
